// src/components/admin/settings/StripeSettings.jsx
import React, { useState } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import { Shield, Eye, EyeOff, CheckCircle } from 'lucide-react';
import styles from '../../../styles/Settings.module.css';
import useTranslation from '../../../hooks/useTranslation';
import { updateStripeConfig, selectStripeConfig } from '../../../store/slices/platformSettingsSlice';
import toast from 'react-hot-toast';

const StripeSettings = () => {
  const dispatch = useDispatch();
  const stripeConfigRedux = useSelector(selectStripeConfig);

  const { translatedText: textStripeConfig } = useTranslation('Stripe Configuration');
  const { translatedText: publishableKey } = useTranslation('Publishable Key');
  const { translatedText: secretKey } = useTranslation('Secret Key');
  const { translatedText: webhookSecret } = useTranslation('Webhook Secret');
  const { translatedText: showKeys } = useTranslation('Show Keys');
  const { translatedText: hideKeys } = useTranslation('Hide Keys');
  const { translatedText: stripeStatus } = useTranslation('Stripe Status');
  const { translatedText: connected } = useTranslation('Connected');
  const { translatedText: activeStatus } = useTranslation('Active');

  const [showStripe, setShowStripe] = useState(false);
  const [localConfig, setLocalConfig] = useState(
    stripeConfigRedux || {
      publishableKey: '',
      secretKey: '',
      webhookSecret: ''
    }
  );

  React.useEffect(() => {
    if (stripeConfigRedux) {
      setLocalConfig(stripeConfigRedux);
    }
  }, [stripeConfigRedux]);

  const handleSave = async () => {
    try {
      await dispatch(updateStripeConfig(localConfig)).unwrap();
      toast.success('Stripe configuration saved!');
    } catch (error) {
      toast.error(error || 'Failed to save settings');
    }
  };

  return (
    <div className={styles.settingsCard}>
      <h3>
        <Shield size={20} />
        {textStripeConfig || 'Stripe Configuration'}
      </h3>
      <div className={styles.settingsForm}>
        <button 
          className={styles.toggleStripeBtn}
          onClick={() => setShowStripe(!showStripe)}
        >
          {showStripe ? <EyeOff size={16} /> : <Eye size={16} />}
          {showStripe ? (hideKeys || 'Hide Keys') : (showKeys || 'Show Keys')}
        </button>
        <div className={styles.formGroup}>
          <label>{publishableKey || 'Publishable Key'}</label>
          <input
            type={showStripe ? 'text' : 'password'}
            value={localConfig.publishableKey}
            onChange={(e) => setLocalConfig({...localConfig, publishableKey: e.target.value})}
            placeholder="pk_test_..."
          />
          <span className={styles.fieldHint}>Your Stripe publishable key</span>
        </div>
        <div className={styles.formGroup}>
          <label>{secretKey || 'Secret Key'}</label>
          <input
            type={showStripe ? 'text' : 'password'}
            value={localConfig.secretKey}
            onChange={(e) => setLocalConfig({...localConfig, secretKey: e.target.value})}
            placeholder="sk_test_..."
          />
          <span className={styles.fieldHint}>Your Stripe secret key (keep this secure)</span>
        </div>
        <div className={styles.formGroup}>
          <label>{webhookSecret || 'Webhook Secret'}</label>
          <input
            type={showStripe ? 'text' : 'password'}
            value={localConfig.webhookSecret}
            onChange={(e) => setLocalConfig({...localConfig, webhookSecret: e.target.value})}
            placeholder="whsec_..."
          />
          <span className={styles.fieldHint}>Your Stripe webhook signing secret</span>
        </div>
        <div className={styles.stripeStatus}>
          <div className={styles.statusItem}>
            <CheckCircle size={16} color="#10B981" />
            <span>{stripeStatus || 'Stripe API Connection'}: <strong>{connected || 'Connected'}</strong></span>
          </div>
          <div className={styles.statusItem}>
            <CheckCircle size={16} color="#10B981" />
            <span>Webhooks: <strong>{activeStatus || 'Active'}</strong></span>
          </div>
        </div>
        <button className={styles.saveBtn} onClick={handleSave}>Save Changes</button>
      </div>
    </div>
  );
};

export default StripeSettings;